/** Расчёт рейтинга и сортировка для сводки MRP / MCL / FAMQ / GZ / Media */

import { getMrpRatingConfig, calcMetricRatePoints } from './mrpRatingSettings';
import { SHIFTS_AND_ONLINE_ENABLED } from '../constants/featureFlags';
import { calcActivityScore } from './mrpStatisticsMetrics';

const WEEKS_IN_YEAR = 52;

function cfg() {
  return getMrpRatingConfig();
}

export const MRP_MONTH_LABELS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

export const MRP_MONTH_LABELS_SHORT = [
  'янв', 'фев', 'мар', 'апр', 'май', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];

function normalizeMonthlyCounts(arr) {
  const months = cfg().yearMonths;
  const out = Array(months).fill(0);
  if (!Array.isArray(arr)) return out;
  for (let i = 0; i < months && i < arr.length; i += 1) {
    out[i] = Number(arr[i]) || 0;
  }
  return out;
}

/** Штрафы за месяц: AFK блоками по 15, выговор −0,5 за шт. */
export function calcMrpMonthDisciplinePenalties(afkKicks, reprimands) {
  const c = cfg();
  const afk = Number(afkKicks) || 0;
  const rep = Number(reprimands) || 0;
  const afkBlocks = Math.floor(afk / c.afkPerBlock);
  return {
    afkKicks: afk,
    reprimands: rep,
    afkBlocks,
    afkPenalty: afkBlocks * c.afkPenalty,
    reprimandPenalty: rep * c.reprimandPenalty,
    summedByMonth: false,
    monthlyPenalties: [],
  };
}

/**
 * Год: сумма штрафов как если бы считали каждый месяц отдельно
 * (15 AFK в январе и 15 в феврале → −0,5 + −0,5, а не −0,5 за 30 за год).
 */
export function calcMrpYearDisciplinePenalties(
  afkKicksByMonth,
  reprimandsByMonth,
  fallbackTotals = null
) {
  const hasMonthly =
    Array.isArray(afkKicksByMonth) && Array.isArray(reprimandsByMonth);

  if (!hasMonthly && fallbackTotals) {
    return calcMrpMonthDisciplinePenalties(
      fallbackTotals.afkKicks,
      fallbackTotals.reprimands
    );
  }

  const afkMonths = normalizeMonthlyCounts(afkKicksByMonth);
  const repMonths = normalizeMonthlyCounts(reprimandsByMonth);

  let afkPenalty = 0;
  let reprimandPenalty = 0;
  let afkKicks = 0;
  let reprimands = 0;
  const monthlyPenalties = [];

  const months = cfg().yearMonths;
  for (let i = 0; i < months; i += 1) {
    const monthAfk = afkMonths[i];
    const monthRep = repMonths[i];
    afkKicks += monthAfk;
    reprimands += monthRep;

    const monthDisc = calcMrpMonthDisciplinePenalties(monthAfk, monthRep);
    afkPenalty += monthDisc.afkPenalty;
    reprimandPenalty += monthDisc.reprimandPenalty;

    if (monthDisc.afkPenalty > 0 || monthDisc.reprimandPenalty > 0) {
      monthlyPenalties.push({
        month: i + 1,
        afkKicks: monthAfk,
        reprimands: monthRep,
        afkPenalty: monthDisc.afkPenalty,
        reprimandPenalty: monthDisc.reprimandPenalty,
      });
    }
  }

  return {
    afkKicks,
    reprimands,
    afkBlocks: null,
    afkPenalty,
    reprimandPenalty,
    summedByMonth: true,
    monthlyPenalties,
  };
}

export function isSeniorRole(role) {
  const r = (role || '').toLowerCase();
  return (
    r.includes('senior') ||
    r.includes('chief') ||
    r.includes('curator') ||
    r.includes('deputy')
  );
}

export function getWeeklyNorm(role) {
  const c = cfg();
  return isSeniorRole(role) ? c.weeklyNormSenior : c.weeklyNormRegular;
}

export function getWeeklyPunishmentsNorm(role) {
  const c = cfg();
  return isSeniorRole(role) ? c.weeklyPunishmentsNormSenior : c.weeklyPunishmentsNormRegular;
}

function getWeeksInPeriod(periodType, year, month) {
  if (periodType === 'year') return WEEKS_IN_YEAR;
  return new Date(year, month, 0).getDate() / 7;
}

function getPeriodDateBounds(periodType, year, month) {
  if (periodType === 'month') {
    return {
      start: new Date(year, month - 1, 1, 0, 0, 0, 0),
      end: new Date(year, month, 0, 23, 59, 59, 999),
    };
  }
  return {
    start: new Date(year, 0, 1, 0, 0, 0, 0),
    end: new Date(year, 11, 31, 23, 59, 59, 999),
  };
}

/** Начало текущего срока на посту (первичный найм или последнее восстановление) */
export function getTenureStart(mod) {
  let startMs = null;

  if (mod.created_at) {
    const created = new Date(mod.created_at);
    if (!Number.isNaN(created.getTime())) startMs = created.getTime();
  }
  if (mod.restored_at) {
    const restored = new Date(mod.restored_at);
    if (!Number.isNaN(restored.getTime())) {
      startMs = startMs == null ? restored.getTime() : Math.max(startMs, restored.getTime());
    }
  }

  return startMs != null ? new Date(startMs) : null;
}

/** Доля периода и недели, когда модератор был на посту */
export function getModeratorActiveSpan(mod, periodType, year, month) {
  const { start: periodStart, end: periodEnd } = getPeriodDateBounds(periodType, year, month);
  let activeStart = periodStart;
  let activeEnd = periodEnd;

  const tenureStart = getTenureStart(mod);
  if (tenureStart && tenureStart > activeStart) {
    activeStart = tenureStart;
  }
  if (mod.deleted_at) {
    const deleted = new Date(mod.deleted_at);
    if (!Number.isNaN(deleted.getTime()) && deleted < activeEnd) {
      activeEnd = deleted;
    }
  }

  if (activeEnd < activeStart) {
    return { activeDays: 0, activeWeeks: 0, activeFraction: 0 };
  }

  const periodMs = periodEnd.getTime() - periodStart.getTime() + 1;
  const activeMs = activeEnd.getTime() - activeStart.getTime() + 1;
  const activeDays = activeMs / (24 * 60 * 60 * 1000);
  const activeWeeks = activeDays / 7;
  const activeFraction = Math.min(1, Math.max(0, activeMs / periodMs));

  return { activeDays, activeWeeks, activeFraction };
}

/** План за период: месяц — по длине месяца; год — общий календарный (52 нед.), без урезания по сроку на посту */
export function getExpectedHours(periodType, year, month, role) {
  const raw = getWeeksInPeriod(periodType, year, month) * getWeeklyNorm(role);
  return Math.round(raw);
}

/** План часов с учётом срока на посту внутри месяца (неполный месяц после найма/увольнения/восстановления) */
export function getExpectedHoursForModerator(mod, periodType, year, month, role) {
  if (periodType === 'month') {
    const span = getModeratorActiveSpan(mod, periodType, year, month);
    if (span.activeFraction <= 0) return 0;
    if (span.activeFraction < 1) {
      return Math.round(span.activeWeeks * getWeeklyNorm(role));
    }
  }
  return getExpectedHours(periodType, year, month, role);
}

function monthHasMrpYearRatingActivity({
  minutes = 0,
  punishments = 0,
  afkKicks = 0,
  reprimands = 0,
  chatResponses = 0,
  activityScore = 0,
} = {}) {
  return (
    minutes > 0 ||
    punishments > 0 ||
    afkKicks > 0 ||
    reprimands > 0 ||
    chatResponses > 0 ||
    activityScore > 0
  );
}

/** Месяц входит в годовой рейтинг: была активность или модератор был на посту в этом месяце */
export function shouldIncludeMonthInMrpYearRating(mod, year, month, activity = {}) {
  if (monthHasMrpYearRatingActivity(activity)) return true;
  return wasModeratorActiveInCalendarMonth(mod, year, month);
}

/** Подсказка к строке месяца в годовом разборе рейтинга */
export function formatMrpYearMonthRowHint(entry) {
  const parts = [];
  if (entry.hours > 0) {
    parts.push(
      SHIFTS_AND_ONLINE_ENABLED
        ? `${String(entry.hours).replace('.', ',')} ч онлайн`
        : `${String(entry.hours).replace('.', ',')} действий`
    );
  } else {
    parts.push(SHIFTS_AND_ONLINE_ENABLED ? 'без онлайна' : 'без активности');
  }
  if (entry.punishments > 0) parts.push(`${entry.punishments} нак.`);
  if (entry.afkKicks > 0) parts.push(`${entry.afkKicks} AFK`);
  if (entry.reprimands > 0) parts.push(`${entry.reprimands} выг.`);
  if (entry.chatResponses > 0) parts.push(`${entry.chatResponses} отв.`);
  return parts.join(' · ');
}

export function getExpectedPunishments(periodType, year, month, role) {
  const raw = getWeeksInPeriod(periodType, year, month) * getWeeklyPunishmentsNorm(role);
  return Math.round(raw);
}

/** Часы плана для отображения (целое число) */
export function formatPlanHours(value) {
  if (value == null || Number.isNaN(value)) return '—';
  return String(Math.round(Number(value)));
}

function clampRating(value) {
  const v = Math.max(0, Math.min(cfg().ratingMax, value));
  return Math.round(v * 10) / 10;
}

/** Отображение рейтинга: одна цифра после запятой (1,3 / 4,0) */
export function formatRating(value) {
  if (value == null || Number.isNaN(value)) return '—';
  if (value < 0) return '—';
  const rounded = clampRating(value);
  return rounded.toFixed(1).replace('.', ',');
}

/** +баллы за каждые N ответов в общем чате */
export function calcChatResponseBonus(chatResponses, periodType = 'month') {
  const c = cfg();
  const count = Math.max(0, Math.floor(Number(chatResponses) || 0));
  const step =
    periodType === 'year'
      ? Math.max(1, c.chatResponseStep * c.yearMonths)
      : Math.max(1, c.chatResponseStep);
  const blocks = Math.floor(count / step);
  const bonus = blocks * c.chatResponseBonusPerStep;
  const used = blocks * step;
  const toNext = blocks > 0 ? step - (count - used) : step - count;

  return {
    chatResponses: count,
    chatResponseStep: step,
    chatResponseBlocks: blocks,
    chatResponseBonus: bonus,
    chatResponsesToNextBonus: Math.max(0, Math.ceil(toNext)),
  };
}

/** +0,1 за каждые полные 5 ч сверх плана за период */
export function calcOvertimeBonus(hours, expectedHours) {
  const c = cfg();
  const h = Number(hours) || 0;
  const plan = Math.round(Number(expectedHours) || 0);
  if (plan <= 0 || h <= plan) {
    return {
      expectedHours: plan,
      overtimeHours: 0,
      overtimeBlocks: 0,
      overtimeBonus: 0,
      hoursToNextBonus: plan > 0 ? c.overtimeHoursStep : 0,
    };
  }
  const overtimeHours = Math.round((h - plan) * 10) / 10;
  const overtimeBlocks = Math.floor(overtimeHours / c.overtimeHoursStep);
  const overtimeBonus = overtimeBlocks * c.overtimeBonusPerStep;
  const usedHours = overtimeBlocks * c.overtimeHoursStep;
  const hoursToNextBonus =
    overtimeBlocks > 0
      ? c.overtimeHoursStep - (overtimeHours - usedHours)
      : c.overtimeHoursStep - overtimeHours;

  return {
    expectedHours: plan,
    overtimeHours,
    overtimeBlocks,
    overtimeBonus,
    hoursToNextBonus: Math.max(0, Math.ceil(hoursToNextBonus * 10) / 10),
  };
}

/** Цвет рейтинга: low 0–1,99 · mid 2–3,49 · high 3,5–5 */
export function getRatingColorLevel(rating) {
  const r = Number(rating);
  if (rating == null || Number.isNaN(r) || r < 0) return 'empty';
  if (r >= 3.5) return 'high';
  if (r >= 2) return 'mid';
  return 'low';
}

function ratioUp(value, min, max) {
  if (value <= 0) return 0;
  if (max <= min) return 1;
  return (value - min) / (max - min);
}

function punishmentsRange(rows) {
  const withP = rows.filter((r) => r.punishments > 0);
  if (!withP.length) return { minP: 0, maxP: 0 };
  return {
    minP: Math.min(...withP.map((r) => r.punishments)),
    maxP: Math.max(...withP.map((r) => r.punishments)),
  };
}

/** Сумма бонусов: за каждый достигнутый порог прибавляется указанное число баллов */
function sumThresholdPoints(value, milestones) {
  const v = Number(value) || 0;
  const list = Array.isArray(milestones) ? milestones : [];
  return list.reduce(
    (sum, m) => (v >= m.min ? sum + (Number(m.points) || 0) : sum),
    0
  );
}

/** Совместимость: старые вызовы с knots (с нулём) → те же прибавки по порогам */
function interpolateTierPoints(value, knotsOrMilestones) {
  const list = Array.isArray(knotsOrMilestones) ? knotsOrMilestones : [];
  const milestones =
    list.length > 0 && list[0].min === 0 ? list.slice(1) : list;
  return sumThresholdPoints(value, milestones);
}

function buildMrpThresholdRatingBreakdown(
  hours,
  punishments,
  afkKicks,
  reprimands,
  {
    hourKnots,
    punishKnots,
    hoursMax,
    punishMax,
    hourMilestones,
    punishMilestones,
    discipline,
    expectedHours = 0,
    chatResponses = 0,
    periodType = 'month',
    metrics = null,
    includeOnlineHours = false,
  }
) {
  const h = Number(hours) || 0;
  const p = Number(punishments) || 0;

  const metricRates = cfg().metricRates || cfg().settings?.metric_rates || {};
  const metricPts = calcMetricRatePoints(metrics || {}, metricRates);
  const usingMetricRates = Object.values(metricRates).some(
    (rate) => (Number(rate?.every) || 0) > 0 && (Number(rate?.points) || 0) > 0
  );

  let hoursPoints = 0;
  let punishPoints = 0;
  let chatBonus = {
    chatResponses: Number(chatResponses) || 0,
    chatResponseStep: cfg().chatResponseStep,
    chatResponseBlocks: 0,
    chatResponseBonus: 0,
    chatResponsesToNextBonus: 0,
  };

  if (usingMetricRates && metrics) {
    hoursPoints = includeOnlineHours ? interpolateTierPoints(h, hourMilestones) : 0;
  } else {
    hoursPoints = interpolateTierPoints(h, hourMilestones);
    punishPoints = interpolateTierPoints(p, punishMilestones);
    chatBonus = calcChatResponseBonus(chatResponses, periodType);
  }

  const disc =
    discipline?.period === 'year'
      ? calcMrpYearDisciplinePenalties(
          discipline.afkKicksByMonth,
          discipline.reprimandsByMonth,
          { afkKicks, reprimands }
        )
      : calcMrpMonthDisciplinePenalties(afkKicks, reprimands);

  const overtime = includeOnlineHours
    ? calcOvertimeBonus(h, expectedHours)
    : {
        expectedHours: 0,
        overtimeHours: 0,
        overtimeBlocks: 0,
        overtimeBonus: 0,
        hoursToNextBonus: 0,
      };

  const base = usingMetricRates && metrics
    ? hoursPoints + metricPts.total
    : hoursPoints + punishPoints;

  const total = clampRating(
    Math.max(
      0,
      base + overtime.overtimeBonus + chatBonus.chatResponseBonus - disc.afkPenalty - disc.reprimandPenalty
    )
  );

  return {
    hours: h,
    punishments: p,
    chatResponses: chatBonus.chatResponses,
    afkKicks: disc.afkKicks,
    reprimands: disc.reprimands,
    hoursPoints,
    punishPoints,
    metricPoints: metricPts.total,
    metricPointsByKey: metricPts.byMetric,
    base,
    afkBlocks: disc.afkBlocks,
    afkPenalty: disc.afkPenalty,
    reprimandPenalty: disc.reprimandPenalty,
    summedByMonth: disc.summedByMonth,
    monthlyPenalties: disc.monthlyPenalties,
    expectedHours: overtime.expectedHours,
    overtimeHours: overtime.overtimeHours,
    overtimeBlocks: overtime.overtimeBlocks,
    overtimeBonus: overtime.overtimeBonus,
    hoursToNextOvertimeBonus: overtime.hoursToNextBonus,
    chatResponseStep: chatBonus.chatResponseStep,
    chatResponseBlocks: chatBonus.chatResponseBlocks,
    chatResponseBonus: chatBonus.chatResponseBonus,
    chatResponsesToNextBonus: chatBonus.chatResponsesToNextBonus,
    total,
    hoursMax,
    punishMax,
    hourMilestones,
    punishMilestones,
  };
}

/** MRP месяц */
export function calcMrpMonthRating(
  hours,
  punishments,
  afkKicks,
  reprimands,
  expectedHours = 0,
  chatResponses = 0,
  metrics = null
) {
  return getMrpMonthRatingBreakdown(
    hours,
    punishments,
    afkKicks,
    reprimands,
    expectedHours,
    chatResponses,
    metrics
  ).total;
}

/** Детализация рейтинга MRP за месяц (для панели по клику) */
export function getMrpMonthRatingBreakdown(
  hours,
  punishments,
  afkKicks,
  reprimands,
  expectedHours = 0,
  chatResponses = 0,
  metrics = null
) {
  const m = cfg().month;
  return buildMrpThresholdRatingBreakdown(hours, punishments, afkKicks, reprimands, {
    hourKnots: m.hourKnots,
    punishKnots: m.punishKnots,
    hoursMax: m.hoursMax,
    punishMax: m.punishMax,
    hourMilestones: m.hourMilestones,
    punishMilestones: m.punishMilestones,
    discipline: { period: 'month' },
    expectedHours,
    chatResponses,
    periodType: 'month',
    metrics,
    includeOnlineHours: SHIFTS_AND_ONLINE_ENABLED,
  });
}

/** Модератор был на посту хотя бы часть календарного месяца */
export function wasModeratorActiveInCalendarMonth(mod, year, month) {
  const { start, end } = getPeriodDateBounds('month', year, month);
  let activeStart = start;
  let activeEnd = end;
  const tenureStart = getTenureStart(mod);
  if (tenureStart && tenureStart > activeStart) activeStart = tenureStart;
  if (mod.deleted_at) {
    const deleted = new Date(mod.deleted_at);
    if (!Number.isNaN(deleted.getTime()) && deleted < activeEnd) activeEnd = deleted;
  }
  return activeEnd >= activeStart;
}

/**
 * Годовой рейтинг MRP = среднее месячных рейтингов (каждый месяц — как в месячной сводке).
 */
export function calcMrpYearRatingFromMonthlyStats(mod, year) {
  const months = cfg().yearMonths;
  const minutesByMonth = normalizeMonthlyCounts(mod.minutesByMonth);
  const punishmentsByMonth = normalizeMonthlyCounts(mod.punishmentsByMonth);
  const chatByMonth = normalizeMonthlyCounts(mod.chatResponsesByMonth);
  const afkByMonth = normalizeMonthlyCounts(mod.afkKicksByMonth);
  const repByMonth = normalizeMonthlyCounts(mod.reprimandsByMonth);
  const role = mod.role;

  const monthlyRatings = [];
  for (let i = 0; i < months; i += 1) {
    const monthNum = i + 1;
    const monthlyMetrics = mod.metricsByMonth
      ? Object.fromEntries(
          Object.entries(mod.metricsByMonth).map(([key, values]) => [
            key,
            Array.isArray(values) ? Number(values[i]) || 0 : 0,
          ])
        )
      : null;
    const punishments = monthlyMetrics
      ? (Number(monthlyMetrics.punishments_discord) || 0) + (Number(monthlyMetrics.punishments_as) || 0)
      : punishmentsByMonth[i];
    const afkKicks = afkByMonth[i];
    const reprimands = repByMonth[i];
    const chatResponses = monthlyMetrics
      ? (Number(monthlyMetrics.chat_responses_discord) || 0) + (Number(monthlyMetrics.chat_responses_as) || 0)
      : chatByMonth[i];
    const minutes = minutesByMonth[i];
    const activityScore = monthlyMetrics ? calcActivityScore(monthlyMetrics) : minutes / 60;

    if (
      !shouldIncludeMonthInMrpYearRating(mod, year, monthNum, {
        minutes,
        punishments,
        afkKicks,
        reprimands,
        chatResponses,
        activityScore,
      })
    ) {
      continue;
    }

    const hours = SHIFTS_AND_ONLINE_ENABLED ? minutes / 60 : activityScore;
    const expectedHours = SHIFTS_AND_ONLINE_ENABLED
      ? getExpectedHoursForModerator(mod, 'month', year, monthNum, role)
      : 0;
    const rating = calcMrpMonthRating(
      hours,
      punishments,
      afkKicks,
      reprimands,
      expectedHours,
      chatResponses,
      monthlyMetrics
    );
    monthlyRatings.push({
      month: monthNum,
      rating,
      hours: Math.round(hours * 10) / 10,
      expectedHours,
      punishments,
      afkKicks,
      reprimands,
      chatResponses,
      totalMetrics: monthlyMetrics,
    });
  }

  if (!monthlyRatings.length) {
    return { total: 0, monthlyRatings: [], monthsCounted: 0 };
  }

  const sum = monthlyRatings.reduce((s, m) => s + m.rating, 0);
  return {
    total: clampRating(sum / monthlyRatings.length),
    monthlyRatings,
    monthsCounted: monthlyRatings.length,
  };
}

/** MRP год: среднее месячных рейтингов */
export function calcMrpYearRating(mod, year) {
  return calcMrpYearRatingFromMonthlyStats(mod, year).total;
}

export function getMrpYearRatingBreakdown(
  hours,
  punishments,
  afkKicks,
  reprimands,
  monthlyDiscipline = {},
  expectedHours = 0,
  chatResponses = 0,
  year = null
) {
  const mod = {
    role: monthlyDiscipline.role,
    created_at: monthlyDiscipline.created_at,
    restored_at: monthlyDiscipline.restored_at,
    deleted_at: monthlyDiscipline.deleted_at,
    minutesByMonth: monthlyDiscipline.minutesByMonth,
    punishmentsByMonth: monthlyDiscipline.punishmentsByMonth,
    chatResponsesByMonth: monthlyDiscipline.chatResponsesByMonth,
    metricsByMonth: monthlyDiscipline.metricsByMonth,
    afkKicksByMonth: monthlyDiscipline.afkKicksByMonth,
    reprimandsByMonth: monthlyDiscipline.reprimandsByMonth,
  };
  const y = year || new Date().getFullYear();
  const result = calcMrpYearRatingFromMonthlyStats(mod, y);

  return {
    hours: Number(hours) || 0,
    punishments: Number(punishments) || 0,
    chatResponses: Number(chatResponses) || 0,
    afkKicks: Number(afkKicks) || 0,
    reprimands: Number(reprimands) || 0,
    expectedHours: Math.round(Number(expectedHours) || 0),
    total: result.total,
    fromMonthlyAverage: true,
    monthsCounted: result.monthsCounted,
    monthlyRatings: result.monthlyRatings,
    hoursPoints: 0,
    punishPoints: 0,
    base: 0,
    afkPenalty: 0,
    reprimandPenalty: 0,
    overtimeBonus: 0,
    chatResponseBonus: 0,
    hourMilestones: [],
    punishMilestones: [],
    hoursMax: 0,
    punishMax: 0,
  };
}

export function getCohortRatingBreakdown(value, cohortValues, label) {
  const positive = cohortValues.filter((v) => v > 0);
  const minV = positive.length ? Math.min(...positive) : 0;
  const maxV = positive.length ? Math.max(...positive) : 0;
  const part = value > 0 ? ratioUp(value, minV, maxV) : 0;
  const total = clampRating(part * cfg().ratingMax);
  return { value, minV, maxV, part, total, label };
}

export function formatPeriodRangeTitle(periodType, year, month) {
  const pad = (n) => String(n).padStart(2, '0');
  if (periodType === 'month') {
    const lastDay = new Date(year, month, 0).getDate();
    const from = `${pad(1)}.${pad(month)}.${year}`;
    const to = `${pad(lastDay)}.${pad(month)}.${year}`;
    return `Статистика модераторов с ${from} по ${to}`;
  }
  return `Статистика модераторов с 01.01.${year} по 31.12.${year}`;
}

function mapRowResult(mod, { rating, expectedHours, normPercent, overtimeHours = 0 }) {
  return {
    ...mod,
    rating,
    ratingLabel: formatRating(rating),
    expectedHours: Math.round(expectedHours || 0),
    normPercent: Math.round(normPercent || 0),
    overtimeHours: Math.max(0, Math.round((Number(overtimeHours) || 0) * 10) / 10),
  };
}

/** Месяц MRP: пороговая шкала часов и наказаний, штрафы за AFK и выговоры */
function attachRatingMrpMonth(rows) {
  return rows.map(({ mod, hours, expectedHours, punishments, afkKicks, reprimands, chatResponses }) => {
    const planRatio = hours / Math.max(expectedHours, 1);
    const rating = calcMrpMonthRating(
      hours,
      punishments,
      afkKicks,
      reprimands,
      expectedHours,
      chatResponses,
      mod.totalMetrics || {}
    );
    const normPercent = expectedHours > 0 && hours > 0 ? planRatio * 100 : 0;
    const { overtimeHours } = calcOvertimeBonus(hours, expectedHours);

    return mapRowResult(mod, {
      rating,
      expectedHours,
      normPercent,
      overtimeHours,
    });
  });
}

/** Год MRP: среднее рейтингов по календарным месяцам на посту */
function attachRatingMrpYear(rows, { year }) {
  return rows.map(({ mod, hours, expectedHours }) => {
    const planRatio = hours / Math.max(expectedHours, 1);
    const { total: rating } = calcMrpYearRatingFromMonthlyStats(mod, year);
    const normPercent = expectedHours > 0 && hours > 0 ? planRatio * 100 : 0;
    const { overtimeHours } = calcOvertimeBonus(hours, expectedHours);

    return mapRowResult(mod, {
      rating,
      expectedHours,
      normPercent,
      overtimeHours,
    });
  });
}

function mapRowWithoutRating(mod) {
  return {
    ...mod,
    rating: null,
    ratingLabel: '—',
    expectedHours: 0,
    normPercent: 0,
    overtimeHours: 0,
  };
}

export function attachRating(moderators, { statType, periodType, year, month }) {
  if (statType !== 'mrp') {
    return moderators.map(mapRowWithoutRating);
  }

  const rows = moderators.map((mod) => {
    const metrics = mod.totalMetrics || {};
    const hours = SHIFTS_AND_ONLINE_ENABLED
      ? (mod.totalMinutes || 0) / 60
      : calcActivityScore(metrics);
    const expectedHours = SHIFTS_AND_ONLINE_ENABLED
      ? getExpectedHours(periodType, year, month, mod.role)
      : 0;
    return {
      mod,
      hours,
      expectedHours,
      punishments:
        (Number(metrics.punishments_discord) || 0) +
        (Number(metrics.punishments_as) || 0),
      afkKicks: mod.totalAfkKicks || 0,
      reprimands: mod.totalReprimands || 0,
      chatResponses:
        (Number(metrics.chat_responses_discord) || 0) +
        (Number(metrics.chat_responses_as) || 0),
      afkKicksByMonth: mod.afkKicksByMonth,
      reprimandsByMonth: mod.reprimandsByMonth,
      minutesByMonth: mod.minutesByMonth,
      punishmentsByMonth: mod.punishmentsByMonth,
      chatResponsesByMonth: mod.chatResponsesByMonth,
    };
  });

  const opts = { periodType, year, month };

  return periodType === 'year' ? attachRatingMrpYear(rows, opts) : attachRatingMrpMonth(rows);
}

export function sortRatedModerators(list, statType) {
  const showOnline = SHIFTS_AND_ONLINE_ENABLED && statType === 'mrp';
  const showScreen = statType === 'media';
  const showPunishments = statType !== 'media';
  const sortByRating = statType === 'mrp';

  return [...list].sort((a, b) => {
    if (sortByRating && b.rating !== a.rating) return b.rating - a.rating;
    if (statType === 'junior') {
      const activityDiff = calcActivityScore(b.totalMetrics) - calcActivityScore(a.totalMetrics);
      if (activityDiff !== 0) return activityDiff;
    }
    if (sortByRating) {
      const otA = Number(a.overtimeHours) || 0;
      const otB = Number(b.overtimeHours) || 0;
      if (otB !== otA) return otB - otA;
    }
    if (showOnline) {
      const hA = (a.totalMinutes || 0) / 60;
      const hB = (b.totalMinutes || 0) / 60;
      if (hB !== hA) return hB - hA;
    }
    if (showScreen) {
      const hA = parseFloat(a.totalScreenHours) || 0;
      const hB = parseFloat(b.totalScreenHours) || 0;
      if (hB !== hA) return hB - hA;
    }
    if (showPunishments && (a.totalPunishments || 0) !== (b.totalPunishments || 0)) {
      return (b.totalPunishments || 0) - (a.totalPunishments || 0);
    }
    return (a.nickname || '').localeCompare(b.nickname || '', 'ru');
  });
}

/** Полный список с местами (без фильтра поиска) */
export function rankModerators(moderators, ratingOpts) {
  const { statType } = ratingOpts;
  return sortRatedModerators(attachRating(moderators, ratingOpts), statType).map((mod, index) => ({
    ...mod,
    rank: index + 1,
  }));
}
